/**
 * How a PEF turns a concession period into a year range.
 *
 * Up to PEF v2 the range was counted **inclusively**: a 10-year concession ran
 * `2026–2035`. From **v3** the end year is **exclusive**, so the same 10-year
 * concession runs `2026–2036`.
 *
 * The version is per-answer (`pefVersion` on the answer / detached-evaluation
 * payload) and is owned by the backend. Answers that carry no version predate
 * the field and are v2, so every helper here treats `null`/`undefined` as
 * legacy — never as v3.
 */

/** Assumed version when the wire carries none. */
export const PEF_VERSION_LEGACY = 2;

/** First version whose end year is exclusive. */
export const PEF_VERSION_EXCLUSIVE_END_YEAR = 3;

export function isPefEndYearExclusive(
  pefVersion: number | null | undefined,
): boolean {
  return (pefVersion ?? PEF_VERSION_LEGACY) >= PEF_VERSION_EXCLUSIVE_END_YEAR;
}

/** How many concession years the range `startYear…endYear` represents. */
export function pefYearSpan(
  startYear: number,
  endYear: number,
  pefVersion: number | null | undefined,
): number {
  return endYear - startYear + (isPefEndYearExclusive(pefVersion) ? 0 : 1);
}

/** The end year that makes the range exactly `span` concession years long. */
export function pefEndYearForSpan(
  startYear: number,
  span: number,
  pefVersion: number | null | undefined,
): number {
  return startYear + span - (isPefEndYearExclusive(pefVersion) ? 0 : 1);
}

/** The start year that makes the range exactly `span` concession years long. */
export function pefStartYearForSpan(
  endYear: number,
  span: number,
  pefVersion: number | null | undefined,
): number {
  return endYear - span + (isPefEndYearExclusive(pefVersion) ? 0 : 1);
}
